/**
 * デッキのページ（/decks/:deckId。Issue #953 / #954）。
 * - カード: 版のカードを見る。カードを押すとその 1 枚の型番・レアリティ（同名カードにも当てられる。版は上がらない）。
 *   並べ替え（ドラッグ）・整列 →「並び順を保存」（並びはデッキに 1 つ。版は上がらない）
 * - バージョン: 版の一覧（枚数・前のバージョンとの差（カード名）・説明・使っている試合）。説明を直す・版を押して編集する・消す
 *   （試合で使われていれば断られる）。好きな 2 つを選んで比べる（`GetDeckVersionDiff`。Issue #956）
 * レシピを変えるのは編集ページ（/decks/:deckId/edit）。デッキを消すのも試合で使われていれば断られる。
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import useCoarsePointer from '../components/useCoarsePointer';
import { CardThumb, useLflist } from '../components/deck/deckParts';
import PrintingModal from '../components/deck/PrintingModal';
import { useFitGrid } from '../components/deck/fitGrid';
import DeckDiff from '../components/deck/DeckDiff';
import { isExtraDeckCard, sortDeckCards } from '../utils/cardFacts';
import { moveInList } from '../utils/deckReorder';
import { formatServerDate } from '../utils/serverTime';

const usedText = (used) => (used ?? []).map((u) => `${u.duel_name ?? `記録 #${u.replay_id}`}（P${u.seat}）`).join('、');

export function usedMessage(body) {
  const list = usedText(body?.used);
  return `${body?.error ?? '消せませんでした'}${list ? `\n\n使っている試合: ${list}` : ''}`;
}

export default function DeckPage() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const coarse = useCoarsePointer();
  const lflist = useLflist();
  const [deck, setDeck] = useState(null);
  const [versions, setVersions] = useState([]);
  const [tab, setTab] = useState('cards');
  const [versionId, setVersionId] = useState(null);   // 見ている版（null＝最新）
  const [view, setView] = useState(null);             // GetDeckCards
  const [printing, setPrinting] = useState(null);     // 押したカード（deck_card_id）
  const [editingDesc, setEditingDesc] = useState(null);   // { id, value }
  const [compare, setCompare] = useState({ from: '', to: '', diff: null });
  usePageTitle(deck?.name ?? 'デッキ');

  const loadDeck = useCallback(() => api.project('GetDeck', { deck_id: Number(deckId) })
    .then((r) => { setDeck(r.deck); setVersions(r.versions ?? []); })
    .catch((err) => alert('デッキを読めませんでした: ' + err.message)), [deckId]);
  const loadCards = useCallback(() => api.project('GetDeckCards', { deck_id: versionId ?? Number(deckId) }).then(setView), [deckId, versionId]);
  useEffect(() => { loadDeck(); }, [loadDeck]);
  useEffect(() => { loadCards(); }, [loadCards]);

  // 並べ替え: 下書きの並び（null＝今の並び）。保存するまで何も変わらない
  const [order, setOrder] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  useEffect(() => { setOrder(null); }, [view]);
  const cards = order ?? view?.cards ?? [];
  const main = cards.filter((c) => !isExtraDeckCard(c));
  const extra = cards.filter((c) => isExtraDeckCard(c));
  const move = (from, to) => {
    if (from === null || to === null || isExtraDeckCard(cards[from]) !== isExtraDeckCard(cards[to])) return;   // メインとエクストラはまたがない
    const next = moveInList(cards, from, to);
    if (next !== cards) setOrder(next);
  };
  const { ref: gridRef, center, grid } = useFitGrid(main.length, extra.length);   // 1 行 10 枚のまま画面に収める（編集ページと同じ）
  const isLatest = view && versions[0] && view.version_id === versions[0].id;

  const arrange = () => setOrder(sortDeckCards(cards));
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.project('SetDeckOrder', { deck_id: Number(deckId), slot_ids: cards.map((c) => c.deck_card_id) });
      await loadCards();
    } catch (err) { alert('並び順を保存できませんでした: ' + err.message); }
    finally { setSavingOrder(false); }
  };
  const tapCard = (idx) => setPrinting(cards[idx].deck_card_id);

  const deleteDeck = async () => {
    if (!window.confirm(`「${deck.name}」を消しますか？（全部のバージョン）`)) return;
    const res = await api.projectResult('DeleteDeck', { deck_id: Number(deckId) });
    if (res.ok) navigate('/decks');
    else alert(usedMessage(res.body));
  };

  const deleteVersion = async (v) => {
    const last = versions.length <= 1;
    if (!window.confirm(last ? `最後のバージョンです。「${deck.name}」ごと消しますか？` : `v${v.version_no} を消しますか？`)) return;
    const res = await api.projectResult('DeleteDeckVersion', { version_id: v.id });
    if (!res.ok) { alert(usedMessage(res.body)); return; }
    if (res.body?.deleted_deck) { navigate('/decks'); return; }
    if (versionId === v.id) setVersionId(null);
    loadDeck();
  };

  const runCompare = async () => {
    if (!compare.from || !compare.to) return;
    try {
      const diff = await api.project('GetDeckVersionDiff', { from_version_id: Number(compare.from), to_version_id: Number(compare.to) });
      setCompare((c) => ({ ...c, diff }));
    } catch (err) { alert('比べられませんでした: ' + err.message); }
  };

  const saveDesc = async (v) => {
    const value = editingDesc?.value ?? '';
    setEditingDesc(null);
    try {
      await api.project('SetDeckVersionDescription', { version_id: v.id, description: value });
      loadDeck();
    } catch (err) { alert('直せませんでした: ' + err.message); }
  };

  const renderGrid = (extraSection) => (
    <div className="no-callout" style={grid}>
      {cards.map((c, idx) => (isExtraDeckCard(c) !== extraSection ? null : (
        <div key={c.deck_card_id} style={{ cursor: 'pointer', borderRadius: 3, outline: dragIdx === idx ? '2px dashed #888' : 'none' }}
          onClick={() => tapCard(idx)} data-testid={`deck-card-${idx}`}
          draggable={!coarse} onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()}
          onDrop={() => { move(dragIdx, idx); setDragIdx(null); }} onDragEnd={() => setDragIdx(null)}
          title={`${c.card_name}${c.set_code ? ` ${c.set_code} ${c.rarity ?? ''}` : ''} — 押して型番を選ぶ（ドラッグで並べ替え）`}>
          <CardThumb card={c} lflist={lflist} />
        </div>
      )))}
    </div>
  );

  if (!deck) return <div className="page"><p className="loading">読み込み中...</p></div>;
  const printingCard = printing && cards.find((c) => c.deck_card_id === printing);
  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
          {deck.deck_illust_thumbnail_url && <img src={deck.deck_illust_thumbnail_url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} />}
          <div style={{ minWidth: 0 }}>
            <div className="card-meta"><Link to="/decks">デッキ管理</Link> ／ {deck.player}{deck.enable === false ? '・レシピだけ' : ''}</div>
            <h1 className="page-title" style={{ margin: 0 }}>{deck.name}</h1>
          </div>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => navigate(`/decks/${deckId}/edit${versionId ? `?version=${versionId}` : ''}`)}>レシピを編集</button>
          <button className="btn btn-danger" onClick={deleteDeck}>デッキを削除</button>
        </div>
      </div>

      <nav className="subnav" aria-label="デッキ" style={{ marginBottom: 12 }}>
        {[['cards', 'カード'], ['versions', `バージョン（${versions.length}）`]].map(([k, label]) => (
          <a key={k} href={`#${k}`} className={tab === k ? 'active' : ''} onClick={(e) => { e.preventDefault(); setTab(k); }}>{label}</a>
        ))}
      </nav>

      {tab === 'cards' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <select className="form-select" style={{ width: 'auto' }} value={versionId ?? ''} data-testid="deck-version"
              onChange={(e) => setVersionId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">最新（v{versions[0]?.version_no ?? '?'}）</option>
              {versions.slice(1).map((v) => <option key={v.id} value={v.id}>v{v.version_no}{v.description ? ` ${v.description}` : ''}</option>)}
            </select>
            <span className="card-meta">メイン {main.length} / エクストラ {extra.length}{view?.description ? `・${view.description}` : ''}</span>
            <button className="btn btn-secondary btn-sm" onClick={arrange} disabled={!cards.length} data-testid="arrange" title="種類・レベル順に並べる">整列</button>
            {order && (
              <>
                <button className="btn btn-success btn-sm" onClick={saveOrder} disabled={savingOrder} data-testid="save-order">
                  {savingOrder ? '保存中...' : '並び順を保存'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setOrder(null)}>元に戻す</button>
              </>
            )}
            {!isLatest && view && <span className="card-meta" style={{ color: '#f5d65e' }}>古いバージョンを見ています</span>}
          </div>
          <div ref={gridRef}>
            <div style={center} data-testid="deck-grid-center">
            {main.length > 0 && <><div className="card-meta">メインデッキ（{main.length}）</div>{renderGrid(false)}</>}
            {extra.length > 0 && <><div className="card-meta">エクストラデッキ（{extra.length}）</div>{renderGrid(true)}</>}
            </div>
          </div>
          {cards.length === 0 && <p className="loading">カードがありません。「レシピを編集」で入れてください</p>}
        </>
      )}

      {tab === 'versions' && (
        <>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }} data-testid="deck-compare">
          <span className="card-meta">比べる</span>
          {['from', 'to'].map((k, i) => (
            <span key={k} style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              {i === 1 && <span className="card-meta">→</span>}
              <select className="form-select" style={{ width: 'auto' }} value={compare[k]} data-testid={`compare-${k}`}
                onChange={(e) => setCompare((c) => ({ ...c, [k]: e.target.value, diff: null }))}>
                <option value="">バージョン</option>
                {versions.map((v) => <option key={v.id} value={v.id}>v{v.version_no}{v.description ? ` ${v.description}` : ''}</option>)}
              </select>
            </span>
          ))}
          <button className="btn btn-secondary btn-sm" disabled={!compare.from || !compare.to || compare.from === compare.to} onClick={runCompare}>比べる</button>
        </div>
        {compare.diff && <div className="card" style={{ marginBottom: 12 }}><DeckDiff diff={compare.diff} /></div>}
        <div className="table-wrap">
          <table className="table" data-testid="deck-versions">
            <thead><tr><th>版</th><th>日付</th><th>枚数</th><th>前との差</th><th>説明</th><th>使っている試合</th><th></th></tr></thead>
            <tbody>
              {versions.map((v, i) => (
                <tr key={v.id}>
                  <td>
                    <Link to={`/decks/${deckId}/edit?version=${v.id}`} title="このバージョンを編集" data-testid={`version-edit-${v.version_no}`}>
                      v{v.version_no}{i === 0 ? '（最新）' : ''}
                    </Link>
                  </td>
                  <td>{formatServerDate(v.created_at) || '—'}</td>
                  <td>{v.main_count}{v.extra_count > 0 ? `+${v.extra_count}` : ''}</td>
                  <td><DeckDiff diff={v.diff} /></td>
                  <td>
                    {editingDesc?.id === v.id ? (
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        <input className="form-input" style={{ padding: '2px 6px' }} value={editingDesc.value} autoFocus
                          onChange={(e) => setEditingDesc({ id: v.id, value: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveDesc(v); if (e.key === 'Escape') setEditingDesc(null); }} />
                        <button className="btn btn-sm btn-primary" onClick={() => saveDesc(v)}>保存</button>
                      </span>
                    ) : (
                      <span style={{ cursor: 'pointer' }} title="押して説明を直す" onClick={() => setEditingDesc({ id: v.id, value: v.description ?? '' })}>
                        {v.description || <span className="card-meta">（説明なし）</span>}
                      </span>
                    )}
                  </td>
                  <td>
                    {(v.used_by ?? []).filter((u) => u.duel_id).map((u, k, list) => (
                      <span key={k}><Link to={`/duels/${u.duel_id}`}>{u.duel_name}</Link>（P{u.seat}{u.games ? `・記録 ${u.games} 試合` : ''}）{k < list.length - 1 ? '、' : ''}</span>
                    ))}
                    {!(v.used_by ?? []).length && <span className="card-meta">—</span>}
                  </td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-danger btn-sm" onClick={() => deleteVersion(v)}>削除</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {printingCard && (
        <PrintingModal card={printingCard} slots={cards.filter((c) => c.card_id === printingCard.card_id)}
          onClose={() => setPrinting(null)} onDone={() => { setPrinting(null); loadCards(); }} />
      )}
    </div>
  );
}
